import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Phone, Instagram, MapPin, ArrowUpRight, Loader2, CreditCard } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/content";
import { BookAddOnCheckbox } from "@/components/books/BookAddOnCheckbox";

const FIELDS = [
  { name: "parent_name", label: "Parent / Guardian Name", required: true },
  { name: "phone", label: "Phone Number", required: true },
  { name: "email", label: "Email Address", type: "email", required: true },
  { name: "child_name", label: "Child's Name", required: true },
  { name: "child_age", label: "Child's Age", required: true },
  { name: "address", label: "Home Address or Neighborhood", required: true },
  { name: "pool_type", label: "Pool Type (in-ground, screened, etc.)" },
  { name: "pool_temperature", label: "Approx. Pool Temperature (°F)" },
  { name: "preferred_days", label: "Preferred Days" },
  { name: "preferred_times", label: "Preferred Times" },
];

const inputCls = "w-full rounded-lg bg-white px-4 py-3 font-body text-sm outline-none border transition-colors";
const inputStyle = { borderColor: "rgba(11,27,61,0.2)", color: "var(--navy)" };

export default function Contact() {
  const [form, setForm] = useState({ swim_experience: "", concerns: "", lesson_length: "20-minute", interested_intensive: false, wants_video: false, wants_book: false, heard_from: "" });
  const [submitting, setSubmitting] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [payingKey, setPayingKey] = useState(null);

  useEffect(() => {
    api.get("/lessons").then((r) => setLessons(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      toast.success("Request received! Milly will personally reach out soon.");
      setForm({ swim_experience: "", concerns: "", lesson_length: "20-minute", interested_intensive: false, wants_video: false, wants_book: false, heard_from: "" });
      e.target.reset();
    } catch (err) {
      toast.error("Something went wrong. Please call 561-577-6346.");
    } finally {
      setSubmitting(false);
    }
  };

  const reserve = async (lookup_key) => {
    setPayingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        customer_name: form.parent_name || "",
        customer_email: form.email || "",
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
      setPayingKey(null);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        overline="Request availability"
        title="Let's start your child's"
        italic="swim journey."
        subtitle="Share a few details and Milly will personally reach out to confirm availability. Payment reserves and secures your preferred time."
        image={IMAGES.parentBaby}
        imagePosition="center 78%"
      />

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          {/* FORM */}
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  {FIELDS.map((f) => (
                    <div key={f.name} className={f.name === "address" ? "sm:col-span-2" : ""}>
                      <label className="overline block mb-2" style={{ color: "var(--muted)" }}>{f.label}{f.required && " *"}</label>
                      <input
                        type={f.type || "text"}
                        required={f.required}
                        className={inputCls}
                        style={inputStyle}
                        data-testid={`field-${f.name}`}
                        onChange={(e) => set(f.name, e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="overline block mb-2" style={{ color: "var(--muted)" }}>Swimming Experience *</label>
                  <textarea required rows={2} className={inputCls} style={inputStyle} data-testid="field-swim_experience"
                    placeholder="Beginner, some lessons, comfortable, fearful of water..."
                    onChange={(e) => set("swim_experience", e.target.value)} />
                </div>
                <div>
                  <label className="overline block mb-2" style={{ color: "var(--muted)" }}>Main Swimming Concerns</label>
                  <textarea rows={2} className={inputCls} style={inputStyle} data-testid="field-concerns"
                    onChange={(e) => set("concerns", e.target.value)} />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="overline block mb-2" style={{ color: "var(--muted)" }}>Desired Lesson Length</label>
                    <select className={inputCls} style={inputStyle} data-testid="field-lesson_length" value={form.lesson_length} onChange={(e) => set("lesson_length", e.target.value)}>
                      <option>20-minute</option>
                      <option>30-minute</option>
                      <option>Not sure — recommend for me</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline block mb-2" style={{ color: "var(--muted)" }}>How did you hear about us?</label>
                    <input type="text" className={inputCls} style={inputStyle} data-testid="field-heard_from" onChange={(e) => set("heard_from", e.target.value)} />
                  </div>
                </div>

                <label className="flex items-center gap-3 py-2 cursor-pointer" data-testid="field-intensive">
                  <input type="checkbox" className="w-5 h-5 accent-[var(--gold)]" onChange={(e) => set("interested_intensive", e.target.checked)} />
                  <span className="font-body text-sm" style={{ color: "var(--navy)" }}>I'm interested in the 5-Day Swim Breakthrough Intensive</span>
                </label>

                <label className="flex items-center gap-3 py-2 cursor-pointer" data-testid="field-wants-video">
                  <input type="checkbox" className="w-5 h-5 accent-[var(--gold)]" data-testid="field-wants-video-checkbox" onChange={(e) => set("wants_video", e.target.checked)} />
                  <span className="font-body text-sm" style={{ color: "var(--navy)" }}>Add underwater video progress (+$10 per lesson) — your first video is free</span>
                </label>

                <BookAddOnCheckbox onChange={(v) => set("wants_book", v)} />

                <button type="submit" disabled={submitting} className="btn-primary w-full sm:w-auto" data-testid="booking-submit">
                  {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <>Request Availability <ArrowUpRight size={18} /></>}
                </button>
              </form>
            </Reveal>
          </div>

          {/* RESERVE & PAY + CONTACT */}
          <div className="lg:col-span-5 space-y-6">
            <Reveal delay={0.1}>
              <div className="p-8 rounded-2xl" style={{ backgroundColor: "var(--navy)" }} data-testid="reserve-pay">
                <div className="flex items-center gap-3 mb-2">
                  <CreditCard size={22} style={{ color: "var(--gold)" }} />
                  <h3 className="font-display text-2xl" style={{ color: "var(--cream)" }}>Reserve &amp; Pay</h3>
                </div>
                <p className="font-body font-light text-sm mb-6" style={{ color: "var(--powder)" }}>
                  Secure your spot instantly. Payment confirms your reserved lesson time.
                </p>
                <div className="space-y-3">
                  {lessons.map((l) => (
                    <button key={l.lookup_key} onClick={() => reserve(l.lookup_key)} disabled={!!payingKey}
                      className="w-full flex items-center justify-between p-4 rounded-xl border text-left transition-colors hover:bg-white/5 disabled:opacity-60"
                      style={{ borderColor: "rgba(212,175,55,0.35)" }} data-testid={`reserve-${l.lookup_key}`}>
                      <span>
                        <span className="block font-body text-sm" style={{ color: "var(--cream)" }}>{l.label}</span>
                      </span>
                      <span className="flex items-center gap-2 font-display text-xl" style={{ color: "var(--gold)" }}>
                        {payingKey === l.lookup_key ? <Loader2 size={18} className="animate-spin" /> : `$${l.amount}`}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="font-body font-light text-xs mt-5" style={{ color: "var(--powder)" }}>
                  Test card: 4242 4242 4242 4242 · any future expiry · any CVC.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="p-8 rounded-2xl bg-white border" style={{ borderColor: "rgba(212,175,55,0.25)" }}>
                <h3 className="font-display text-2xl mb-5" style={{ color: "var(--navy)" }}>Prefer to reach out directly?</h3>
                <ul className="space-y-4 font-body">
                  <li><a href="tel:5615776346" className="flex items-center gap-3" style={{ color: "var(--navy)" }}><Phone size={18} style={{ color: "var(--gold)" }} /> 561-577-6346</a></li>
                  <li><a href="https://instagram.com/mila_pilar444" target="_blank" rel="noreferrer" className="flex items-center gap-3" style={{ color: "var(--navy)" }}><Instagram size={18} style={{ color: "var(--gold)" }} /> @mila_pilar444</a></li>
                  <li className="flex items-center gap-3" style={{ color: "var(--navy)" }}><MapPin size={18} style={{ color: "var(--gold)" }} /> Boca Raton &amp; Palm Beach, Florida</li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
